import { Injectable } from '@angular/core';
import { Subject, Observer, Observable } from 'rxjs';
import { WebSocketService, WebSocketServiceConfig, WebSocketConnectionState } from 'types';
import { FingerPrintsServiceState, FpScannerInfo } from './hw-service-model';
import { HWServiceConfig } from './hw-service-config';
import { filter, take } from 'rxjs/operators';

enum ResponseType {
  None,
  ScannerList,
  WaitForScanning1,
  WaitForScanning2,
  WaitForFinger1,
  WaitForFinger2,
  ScanResult1,
  ScanResult2,
  Compare,
}

@Injectable()
export abstract class FingerPrintsServiceFactory {
  abstract create(config: HWServiceConfig): FingerPrintsService;
}

@Injectable()
export class FingerPrintsService {
  private serviceState: FingerPrintsServiceState = FingerPrintsServiceState.Closed;
  private connectionStateSubject: Subject<FingerPrintsServiceState>;
  private socket: WebSocketService;
  private responseType: ResponseType = ResponseType.None;
  private scanResultObserver: Observer<any> | null = null;
  private compareResultObserver: Observer<number> | null = null;
  private qualityLimit: number;
  private list_scanners: Observer<Array<FpScannerInfo>> | null = null;
  private onStartedScanReceivedQue: number = 0;
  private onScanResultReceivedQue: number = 0;
  private scaningResultArray: any[] = [];
  private scanningGoodQuality: any[] = [];

  public constructor(config: HWServiceConfig) {
    this.connectionStateSubject = new Subject<FingerPrintsServiceState>();
    this.socket = new WebSocketService(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getFingerPrintsServiceProtocol()));
    this.socket.messages$.subscribe(
      (message) => {
        this.onSocketMessageReceived(message);
      },
      (error) => {
        this.onSocketMessageReceived({ result: false, error: error });
      },
    );
    this.socket.connectionState$.subscribe(
      (state) => {
        this.onSocketConnectionStateChanged(state);
      },
      (error) => {
        this.connectionStateSubject.error(error);
      },
    );
  }

  public connect(): void {
    if (this.canConnect()) {
      this.socket.connect();
    } else {
      this.notifyOperationDenied('CONNECT');
    }
  }

  public connect$(): Observable<void> {
    const ob = new Observable<void>((sub) => {
      if (this.canConnect()) {
        this.socket.connectionState$
          .pipe(
            filter((s) => s === WebSocketConnectionState.Opened),
            take(1),
          )
          .subscribe((state) => {
            sub.next();
          });
      } else {
        this.notifyOperationDenied<void>('CONNECT', sub);
      }

      this.socket.connect();
    });

    return ob;
  }

  public disconnect(): void {
    if (this.canDisconnect()) {
      this.resetResponseSettings();
      this.socket.disconnect();
    } else {
      this.notifyOperationDenied('DISCONNECT');
    }
  }

  private get isOpened$(): boolean {
    return this.canDisconnect();
  }

  private canConnect(): boolean {
    return this.serviceState === FingerPrintsServiceState.Closed;
  }

  private canDisconnect(): boolean {
    return this.serviceState !== FingerPrintsServiceState.Closed;
  }

  private setServiceState(state: FingerPrintsServiceState): void {
    if (this.serviceState !== state) {
      this.serviceState = state;
      this.connectionStateSubject.next(state);
    }
  }

  private onSocketConnectionStateChanged(state: WebSocketConnectionState): void {
    switch (state) {
      case WebSocketConnectionState.Opened: {
        this.setServiceState(FingerPrintsServiceState.Ready);
        break;
      }
      case WebSocketConnectionState.Connecting: {
        break;
      }
      case WebSocketConnectionState.Disconnecting:
      case WebSocketConnectionState.Closed:
        this.setServiceState(FingerPrintsServiceState.Closed);
        break;
    }
  }

  private onSocketMessageReceived(message: any): void {
    if (message.command === 'stop-capture') {
      return;
    }
    switch (this.responseType) {
      case ResponseType.ScannerList: {
        this.onScannersListReceived(message);
        break;
      }
      case ResponseType.WaitForScanning1: {
        this.onStartedScanReceived(message, 1);
        break;
      }
      case ResponseType.WaitForScanning2: {
        this.onStartedScanReceived(message, 2);
        break;
      }
      case ResponseType.WaitForFinger1: {
        this.onScanningReceived(message, 1);
        break;
      }
      case ResponseType.WaitForFinger2: {
        this.onScanningReceived(message, 2);
        break;
      }
      case ResponseType.ScanResult1: {
        this.onScanResultReceived(message, 1);
        break;
      }
      case ResponseType.ScanResult2: {
        this.onScanResultReceived(message, 2);
        break;
      }
      case ResponseType.Compare: {
        this.onCompareResultReceived(message);
        break;
      }
    }
  }

  private notifyOperationDenied<T>(operation: string, observer: Observer<T> | null = null): void {
    const message = `Cannot perform operation ${operation}. Processing state: ${
      FingerPrintsServiceState[this.serviceState]
    }. Expected response: ${ResponseType[this.responseType]}.`;
    if (observer) {
      observer.error(message);
    } else {
      this.connectionStateSubject.error(message);
    }
  }

  private resetResponseSettings(): void {
    this.scaningResultArray = [];
    this.scanningGoodQuality = [];
    this.scanResultObserver = null;
    this.compareResultObserver = null;
    this.onStartedScanReceivedQue = 0;
    this.onScanResultReceivedQue = 0;
    this.responseType = ResponseType.None;
  }

  private canPerformOperation(): boolean {
    return this.isOpened$ && this.responseType === ResponseType.None;
  }

  public GetScannersList(): Observable<Array<FpScannerInfo>> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.socket.send({ command: 'list-scanners' });
        this.responseType = ResponseType.ScannerList;
        this.list_scanners = observer;
      } else {
        this.notifyOperationDenied('GET_CAMERA_LIST', observer);
      }
    });
  }

  public ScanFinger1(x: number, qualityLimit: number): Observable<any> {
    this.qualityLimit = qualityLimit;
    return new Observable((o) => {
      if (this.canPerformOperation()) {
        this.socket.send({ 'command': 'start-capture', 'scanner-id': x, 'format': 'wsq', 'rate': 7.5, 'limit': 15281 });
        this.responseType = ResponseType.WaitForScanning1;
        this.scanResultObserver = o;
      } else {
        this.notifyOperationDenied('SCAN_FINGER', o);
      }

      return () => {
        if (
          this.responseType === ResponseType.WaitForScanning1 ||
          this.responseType === ResponseType.WaitForFinger1 ||
          this.responseType === ResponseType.ScanResult1
        ) {
          this.stopCapture(x);
          this.resetResponseSettings();
        }
      };
    });
  }

  public ScanFinger2(scannerL: number, scannerR: number, qualityLimit: number): Observable<any> {
    this.qualityLimit = qualityLimit;
    return new Observable((o) => {
      if (this.canPerformOperation()) {
        this.socket.send({ 'command': 'start-capture', 'scanner-id': scannerL, 'format': 'wsq', 'rate': 7.5, 'limit': 15281 });
        setTimeout(() => {
          this.socket.send({ 'command': 'start-capture', 'scanner-id': scannerR, 'format': 'wsq', 'rate': 7.5, 'limit': 15281 });
        }, 30);
        this.responseType = ResponseType.WaitForScanning2;
        this.scanResultObserver = o;
      } else {
        this.notifyOperationDenied('SCAN_FINGER', o);
      }
    });
  }

  public Compare(a: string, b: string): Observable<number> {
    return new Observable((o) => {
      if (this.canPerformOperation()) {
        this.socket.send({ 'command': 'compare', 'wsq-a': a, 'wsq-b': b });
        this.responseType = ResponseType.Compare;
        this.compareResultObserver = o;
      } else {
        this.notifyOperationDenied('COMPARE', o);
      }
    });
  }

  private stopCapture(scannerId: number) {
    this.socket.send({ 'command': 'stop-capture', 'scanner-id': scannerId });
  }

  private getScanResult(x1: number, x2: number | void) {
    if (this.canPerformOperation()) {
      this.socket.send({ 'command': 'get-image', 'scanner-id': x1 });
      if (x2 != undefined) {
        this.responseType = ResponseType.ScanResult2;
        setTimeout(() => {
          this.socket.send({ 'command': 'get-image', 'scanner-id': x2 });
        }, 30);
      } else {
        this.responseType = ResponseType.ScanResult1;
      }
    }
  }

  private onScannersListReceived(message: any) {
    if (!this.list_scanners) {
      return;
    }
    this.list_scanners.next(message.scanners.map((s: FpScannerInfo) => new FpScannerInfo(Number(s.id), String(s.tag))));
    this.list_scanners.complete();
    this.resetResponseSettings();
  }

  private onStartedScanReceived(message: any, mode: number) {
    if (message.command === 'start-capture') {
      this.onStartedScanReceivedQue++;
    }
    if (this.onStartedScanReceivedQue === mode) {
      if (mode === 1) {
        this.responseType = ResponseType.WaitForFinger1;
      } else {
        this.responseType = ResponseType.WaitForFinger2;
      }
    }
  }

  private onScanningReceived(message: any, mode: number) {
    if (message.command === 'capture') {
      if (!this.scanResultObserver) {
        return;
      }
      this.scaningResultArray.push(message['scanner-id']);
      if (message.quality < this.qualityLimit) {
        this.scanningGoodQuality.push(message['scanner-id']);
      }
      this.scanResultObserver.next(this.scaningResultArray);
      if (this.scaningResultArray.length === mode) {
        if (this.scanningGoodQuality.length === 0) {
          this.scanResultObserver.next('stop_bad_quality!');
          this.scanResultObserver.complete();
          this.resetResponseSettings();
        } else {
          this.responseType = ResponseType.None;
          this.getScanResult(this.scanningGoodQuality[0], this.scanningGoodQuality[1]);
        }
      }
    }
  }

  private onScanResultReceived(message: any, mode: number) {
    if (!this.scanResultObserver) {
      return;
    }
    if (message.result) {
      let answer = new FingerPrintServiceResponse(message['scanner-id'], message.image);
      if (answer.image != undefined) {
        if (this.onScanResultReceivedQue === 0 && mode === 2) {
          this.scanResultObserver.next(answer);
          this.onScanResultReceivedQue++;
        } else {
          this.scanResultObserver.next(answer);
          if (this.scanningGoodQuality.length < this.scaningResultArray.length) {
            let badScannerId = this.scaningResultArray.find((x) => !this.scanningGoodQuality.includes(x));
            let badAnswer = new FingerPrintServiceResponse(badScannerId, '');
            this.scanResultObserver.next(badAnswer);
          }
          this.scanResultObserver.complete();
          this.resetResponseSettings();
        }
      }
    } else {
      this.scanResultObserver.error(message);
      this.resetResponseSettings();
    }
  }

  private onCompareResultReceived(message: any) {
    if (!this.compareResultObserver) {
      return;
    }

    if (message.result) {
      const match = message.match;
      this.compareResultObserver.next(match);
      this.compareResultObserver.complete();
    } else {
      this.compareResultObserver.error(message);
    }

    this.resetResponseSettings();
  }
}

export class FingerPrintServiceResponse {
  scannerId: number;
  image: string;

  constructor(scannerId: number, image: string) {
    this.scannerId = scannerId;
    this.image = image;
  }
}
